// Questionnaire v2 — pure helpers shared by the public fill page, the template
// builder (and its preview), the staff answer view and the client portal's
// read-only copy (docs/specs/questionnaire-v2-civil-rights.md).
//
// PURE: no API client, no DOM.  The public page and the portal import this
// file, so it must never import services/api.js (staff auth) or
// services/portalApi.js.
//
// The rules here MIRROR api/src/lib/questionnaireSchema.js — visibility,
// what counts as answered, the snapshot node defaults.  The server is the
// authority (it re-validates every save); these copies exist so the page never
// sends what the server would refuse and never shows a question the server
// would call hidden.  If you change one, change both.

export const QUESTION_TYPES = ['short', 'date', 'yes_no', 'choice', 'long', 'repeat', 'upload', 'contact_field']
export const TYPE_LABELS = {
  short: 'Short answer',
  date: 'Date',
  yes_no: 'Yes / No',
  choice: 'Multiple choice',
  long: 'Long answer',
  repeat: 'Repeating rows',
  upload: 'Upload step',
  contact_field: 'Contact field',
}
// Types that may have follow-ups, and those whose answer can trigger one.
export const PARENT_TYPES = ['short', 'date', 'yes_no', 'choice', 'long']
export const CONDITIONAL_PARENT_TYPES = ['yes_no', 'choice']
export const ANSWERABLE_TYPES = ['short', 'date', 'yes_no', 'choice', 'long', 'repeat']
export const MAX_DEPTH = 3

export const CONTACT_KEYS = ['full_name', 'street_address', 'city_state_zip', 'phone', 'cell_phone', 'email']
export const PREFILLABLE_CONTACT_KEYS = ['full_name', 'city_state_zip', 'phone', 'email']
export const CONTACT_KEY_LABELS = {
  full_name: 'Full name',
  street_address: 'Street address',
  city_state_zip: 'City / State / Zip',
  phone: 'Telephone number',
  cell_phone: 'Cell phone number',
  email: 'Email address',
}

export const YES_NO_LABELS = { yes: 'Yes', no: 'No', unknown: "Don't know" }

// Defaults the server applies when it snapshots a question (snapshotNode).
export const LIMIT_DEFAULTS = {
  SHORT_DEFAULT: 500, SHORT_CEILING: 2000, LONG_DEFAULT: 6000, LONG_CEILING: 10000,
  ROW_FIELD_DEFAULT: 300, ROW_FIELD_CEILING: 2000, ROW_FIELDS_MAX: 6,
  MAX_ROWS_DEFAULT: 10, MAX_ROWS_CEILING: 20, CHOICES_MIN: 2, CHOICES_MAX: 20,
  LABEL_MAX: 2000, HELP_MAX: 1000, NUMBER_LABEL_MAX: 20, CONTACT_VALUE_MAX: 300, MAX_QUESTIONS: 100,
}

const ROW_ID_RE = /^[A-Za-z0-9_-]{1,24}$/

// ─── Tree walking ─────────────────────────────────────────────────────────
// index: Map String(id) → { node, parent, depth } in display (DFS) order.
export function indexTree (nodes) {
  const byId = new Map()
  const order = []
  const walk = (list, parent, depth) => {
    if (!Array.isArray(list) || depth > MAX_DEPTH + 1) return
    for (const node of list) {
      if (!node || node.id == null) continue
      const k = String(node.id)
      if (byId.has(k)) continue
      byId.set(k, { node, parent, depth })
      order.push(node)
      walk(node.children, node, depth + 1)
    }
  }
  walk(nodes, null, 1)
  return { byId, order }
}

// A node is visible when its parent is visible (top level always is) and, if
// it names a show_when, the parent's answer equals it.  Mirrors visibilityOf.
export function makeVisibility (index, answers) {
  const memo = new Map()
  const a = answers || {}
  const vis = (node) => {
    if (!node) return false
    const k = String(node.id)
    if (memo.has(k)) return memo.get(k)
    const entry = index.byId.get(k)
    let v = true
    if (entry && entry.parent) {
      v = vis(entry.parent)
      if (v && node.show_when != null) v = a[String(entry.parent.id)] === node.show_when
    }
    memo.set(k, v)
    return v
  }
  return vis
}

function isBlankCell (v) { return v === undefined || v === null || (typeof v === 'string' && v.trim() === '') }

export function rowIsBlank (row) {
  if (!row || typeof row !== 'object') return true
  return Object.keys(row).every(k => k === '_id' || isBlankCell(row[k]))
}

// The answers to SEND: visible + answerable + non-empty.  Hidden answers stay
// in the page's memory (the caller keeps the full map); repeat rows go out as
// displayed (blank cells and blank rows included — the server drops them), so
// a row error's index matches the row on screen.
export function visibleAnswers (questions, answers) {
  const index = indexTree(questions)
  const vis = makeVisibility(index, answers)
  const out = {}
  for (const node of index.order) {
    if (!ANSWERABLE_TYPES.includes(node.type) || !vis(node)) continue
    const k = String(node.id)
    const v = answers ? answers[k] : undefined
    if (node.type === 'repeat') {
      if (Array.isArray(v) && v.some(r => !rowIsBlank(r))) {
        out[k] = v.map(r => {
          const row = { _id: r._id }
          for (const f of node.row_fields || []) row[f.key] = r[f.key] == null ? '' : r[f.key]
          return row
        })
      }
      continue
    }
    if (typeof v === 'string' && v.trim() !== '') out[k] = v
  }
  return out
}

export function isAnswered (node, answers, contact) {
  if (!node) return false
  if (node.type === 'contact_field') {
    const c = contact && contact[node.contact_key]
    return !!(c && (c.confirmed === true || (typeof c.value === 'string' && c.value.trim())))
  }
  const v = answers ? answers[String(node.id)] : undefined
  if (Array.isArray(v)) return v.some(r => !rowIsBlank(r))
  return typeof v === 'string' ? v.trim() !== '' : v != null
}

// Required + visible + unanswered (mirrors missingRequired).  The upload
// question needs the files count, which only the server knows for sure; pass
// `uploadCount` when the page has one, else upload questions are skipped here
// and left to the server's answer.
export function missingRequired (questions, { answers = {}, contact = {}, uploadCount = null } = {}) {
  const index = indexTree(questions)
  const vis = makeVisibility(index, answers)
  const missing = []
  for (const node of index.order) {
    if (!node.is_required || !vis(node)) continue
    if (node.type === 'upload') {
      if (uploadCount != null && uploadCount < 1) missing.push(node)
      continue
    }
    if (!isAnswered(node, answers, contact)) missing.push(node)
  }
  return missing
}

// Contact answers to SEND: only the fields the client touched.
export function contactPayload (fields, contact) {
  const out = {}
  for (const f of fields || []) {
    const a = contact ? contact[f.key] : null
    if (!a) continue
    if (a.confirmed === true && f.prefill_masked) out[f.key] = { confirmed: true }
    else if (typeof a.value === 'string' && a.value.trim()) out[f.key] = { value: a.value }
  }
  return out
}

// ─── Text helpers ─────────────────────────────────────────────────────────
export function wordCount (text) {
  if (typeof text !== 'string') return 0
  const t = text.trim()
  return t ? t.split(/\s+/).length : 0
}

export function newRowId () {
  const rand = Math.random().toString(36).slice(2, 10)
  const t = Date.now().toString(36).slice(-6)
  return `r${t}${rand}`.slice(0, 24)
}
export function isValidRowId (s) { return typeof s === 'string' && ROW_ID_RE.test(s) }

// Rows as displayed: the saved rows (each keeps its _id), or one blank row.
export function rowsForDisplay (node, saved) {
  const list = Array.isArray(saved) ? saved.filter(r => r && typeof r === 'object') : []
  const rows = list.map(r => (isValidRowId(r._id) ? r : { ...r, _id: newRowId() }))
  return rows.length ? rows : [blankRow(node)]
}
export function blankRow (node) {
  const r = { _id: newRowId() }
  for (const f of (node && node.row_fields) || []) r[f.key] = ''
  return r
}

// YYYY-MM-DD → "March 3, 2024" without any time-zone shift (a DATE answer is a
// calendar day, never an instant — never `new Date('2024-03-03')`).
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
export function formatAnswerDate (ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''))
  if (!m) return String(ymd || '')
  const mo = parseInt(m[2], 10)
  if (mo < 1 || mo > 12) return String(ymd)
  return `${MONTHS[mo - 1]} ${parseInt(m[3], 10)}, ${m[1]}`
}

// The visitor's own calendar day (the public page has no firm zone; the
// server allows one day of slack for a client ahead of the firm).
export function localToday (now = new Date()) {
  const y = now.getFullYear()
  const mo = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${mo}-${d}`
}
export function addYears (ymd, years) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd)
  if (!m) return ymd
  return `${parseInt(m[1], 10) + years}-${m[2]}-${m[3]}`
}

export function questionTitle (node) {
  if (!node) return ''
  return node.number_label ? `${node.number_label}. ${node.label}` : node.label
}

export function answerLabel (node, value) {
  if (value == null || value === '') return ''
  if (node.type === 'yes_no') return YES_NO_LABELS[value] || String(value)
  if (node.type === 'choice') {
    const hit = ((node.options && node.options.choices) || []).find(c => c.key === value)
    return hit ? hit.label : String(value)
  }
  if (node.type === 'date') return formatAnswerDate(value)
  return String(value)
}

// "Shown when 8(B) is Yes" — for the builder and the staff view.
export function conditionText (node, parent) {
  if (!node || !parent || node.show_when == null) return null
  const who = parent.number_label || 'the question above'
  return `Shown when ${who} is “${answerLabel(parent, node.show_when)}”`
}

// ─── Validation messages (server rule codes → sentences; never values) ───
export function ruleMessage (rule, node) {
  switch (rule) {
    case 'required': return 'Please answer this question.'
    case 'type': return 'This answer is not in a form we can save.  Please re-enter it.'
    case 'too_long': return 'This answer is too long.  Please shorten it.'
    case 'not_a_number': return 'Please enter a number, using digits only.'
    case 'enum': return 'Please choose one of the options.'
    case 'date_invalid': return 'Please enter a real date.'
    case 'date_out_of_range':
      return node && node.options && node.options.allow_future
        ? 'Please check this date — it is too far from today.'
        : 'Please check this date — it cannot be in the future or before 1900.'
    case 'rows_max': return 'There are more entries than this question can hold.'
    case 'row_shape': case 'row_id': case 'row_field_unknown': return 'One of these entries could not be saved.  Please re-enter it.'
    case 'hidden': return 'This question no longer applies to your answers, so it was not saved.'
    case 'nothing_to_confirm': case 'unknown_contact_field': return 'Please type this detail instead.'
    case 'aggregate_too_long': return 'Your answers are longer than this questionnaire can hold.  Please shorten some of them.'
    case 'unknown_question': case 'not_answerable':
    default: return 'This answer could not be saved.  Please reload the page and try again.'
  }
}

// ─── Builder: flat rows (GET /questionnaire-templates/:id) → tree ─────────
// Mirrors the server's snapshotNode defaults, so the builder's preview shows
// what a client will see once the template is sent.
export function snapshotNodeFromRow (q) {
  const n = {
    id: q.id,
    parent_id: q.parent_id ?? null,
    show_when: q.show_when ?? null,
    type: q.type,
    number_label: q.number_label || null,
    label: q.label,
    help_text: q.help_text || null,
    is_required: !!q.is_required,
    options: q.options && typeof q.options === 'object' && !Array.isArray(q.options) ? q.options : {},
    sort_order: q.sort_order ?? 0,
  }
  if (q.type === 'repeat') { n.row_fields = Array.isArray(q.row_fields) ? q.row_fields : []; n.max_rows = q.max_rows || LIMIT_DEFAULTS.MAX_ROWS_DEFAULT }
  if (q.type === 'long') { n.word_guide_min = q.word_guide_min ?? null; n.word_guide_max = q.word_guide_max ?? null }
  if (q.type === 'short') n.max_chars = q.max_chars || LIMIT_DEFAULTS.SHORT_DEFAULT
  if (q.type === 'long') n.max_chars = q.max_chars || LIMIT_DEFAULTS.LONG_DEFAULT
  if (q.type === 'contact_field') {
    n.contact_key = q.contact_key
    n.prefillable = PREFILLABLE_CONTACT_KEYS.includes(q.contact_key)
    n.max_chars = LIMIT_DEFAULTS.CONTACT_VALUE_MAX
  }
  n.children = []
  return n
}

export function buildTreeFromRows (rows) {
  const list = (rows || []).slice().sort((a, b) => ((a.sort_order ?? 0) - (b.sort_order ?? 0)) || (a.id - b.id))
  const nodes = new Map(list.map(q => [q.id, snapshotNodeFromRow(q)]))
  const top = []
  for (const q of list) {
    const n = nodes.get(q.id)
    if (q.parent_id == null) top.push(n)
    else if (nodes.has(q.parent_id)) nodes.get(q.parent_id).children.push(n)
  }
  return top
}

// Every id in display order — what POST …/questions/reorder wants.
export function dfsIds (tree) {
  return indexTree(tree).order.map(n => n.id)
}

// Swap a question with its previous (-1) or next (+1) sibling; returns the
// new full id order, or null when it cannot move that way.
export function moveAmongSiblings (tree, id, dir) {
  const clone = (list) => list.map(n => ({ ...n, children: clone(n.children || []) }))
  const copy = clone(tree)
  const find = (list) => {
    const i = list.findIndex(n => n.id === id)
    if (i >= 0) return { list, i }
    for (const n of list) { const hit = find(n.children); if (hit) return hit }
    return null
  }
  const hit = find(copy)
  if (!hit) return null
  const j = hit.i + dir
  if (j < 0 || j >= hit.list.length) return null
  const tmp = hit.list[hit.i]; hit.list[hit.i] = hit.list[j]; hit.list[j] = tmp
  return dfsIds(copy)
}

// Depth of a node in the tree (1 = top level), or 0 when absent.
export function depthOf (tree, id) {
  const e = indexTree(tree).byId.get(String(id))
  return e ? e.depth : 0
}

export function descendantCount (node) {
  let n = 0
  for (const c of (node && node.children) || []) n += 1 + descendantCount(c)
  return n
}
